import React from "react";
import { matchesBoolean, matchesString } from "LIB/filters";
import { TitleWithFilters } from "MODULES/shared/components/Pagination";
import { SearchPropTypes } from "MODULES/shared/components";
import withCurrentUser, {
  WithCurrentUser
} from "MODULES/session/decorators/withCurrentUser";
import {
  BUYER,
  SALES,
  ADMIN,
  REPAIR,
  QUALITY_CONTROL,
  ACCOUNTING,
  CAN_VIEW_ALL_INVENTORY
} from "MODULES/users/constants";
import { ATL, BTL, BBTL, ZTL, SATL, TIER_TYPE } from "MODULES/parts/constants";
import {
  AS_REMOVED,
  DO_NOT_LIST,
  DO_NOT_SELL,
  GONE,
  LIST,
  PARTS_UNIT,
  QC_HOLD,
  REPAIR_AND_HOLD,
  REPAIR_AND_HOLD_LEAD_TIME,
  REPAIRED,
  SCRAP,
  STORE_OWNER_ID
} from "MODULES/inventories/constants";
import {
  BEYOND_ECON_REPAIR,
  CONSIGNMENT,
  EMA
} from "MODULES/Receiving/constants";
import { properCase } from "LIB/formatters/text";
import { hasAnyOfRoles, hasPermission } from "MODULES/users/helpers";

const filterTags = [
  {
    label: "My Inventory",
    key: "my_inventory",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Color Bar = Green",
    key: "sellable_green_status",
    matchesTarget: matchesString(LIST)
  },
  {
    label: "Discrepancies",
    key: "open_discrepancies"
  },
  {
    label: "Location @Shop",
    key: "location_at_shop"
  },
  {
    label: "Color Bar = Yellow",
    key: "sellable_yellow_status",
    matchesTarget: matchesString(DO_NOT_LIST)
  },
  {
    label: "Color Bar = Red",
    key: "sellable_red_status",
    matchesTarget: matchesString(DO_NOT_SELL)
  },
  {
    label: "No SRP",
    key: "no_srp"
  },
  {
    label: "Show Only Remaining Inventory",
    key: "not_gone"
  },
  {
    label: "Show Inventory with Remaining Cost",
    key: "has_cost"
  },
  {
    label: "Overhauled",
    key: "overhauled"
  },
  {
    label: "Repaired",
    key: REPAIRED
  },
  {
    label: "Inspected",
    key: "inspected"
  },
  {
    label: "New",
    key: "new_item"
  },
  {
    label: "As Removed",
    key: AS_REMOVED
  },
  {
    label: "Beyond Repair",
    key: BEYOND_ECON_REPAIR
  },
  {
    label: "Parts Unit",
    key: PARTS_UNIT
  },
  {
    label: "Repair & Hold",
    key: REPAIR_AND_HOLD
  },
  {
    label: "Repair & Hold - LT",
    key: REPAIR_AND_HOLD_LEAD_TIME
  },
  {
    label: "Scrap",
    key: SCRAP
  },
  {
    label: "Show Inventory with QC Hold",
    key: "qc_status",
    matchesTarget: matchesString(QC_HOLD)
  },
  {
    label: "Gone From Stock",
    key: "out_of_stock"
  },
  {
    label: "Sold",
    key: "sold"
  },
  {
    label: "< 6 mos",
    key: "date_received_under_6_mos",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "6 to 12 mos",
    key: "date_received_6_to_12_mos",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "12 to 18 mos",
    key: "date_received_12_to_18_mos",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "> 18 mos",
    key: "date_received_over_18_mos",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Reserved For Repair",
    key: "repair_status",
    matchesTarget: matchesString("reserve_for_repair")
  },
  {
    label: "Do Not List",
    key: "do_not_list",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Do Not Sell",
    key: "do_not_sell",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Do Not Exchange",
    key: "do_not_exchange"
  },
  {
    label: "Reserved For Sale",
    key: "status",
    matchesTarget: matchesString("reserve_for_sale,on_order_for_sale")
  },
  {
    label: "Tag Date > 5 Years",
    key: "tag_date",
    matchesTarget: matchesString("over_5_years")
  },
  {
    label: "Show All Scrapped Inventory",
    key: "condition",
    matchesTarget: matchesString(SCRAP)
  },
  {
    label: "Gone",
    key: "status",
    matchesTarget: matchesString(GONE)
  },
  {
    label: "Has Scrap Report",
    key: "has_scrap_report"
  },
  {
    label: "Tier Type = 4 Star",
    key: TIER_TYPE,
    matchesTarget: matchesString(ATL),
    withAuthorization: { roles: [BUYER, SALES, ADMIN] }
  },
  {
    label: "Tier Type = 3 Star",
    key: TIER_TYPE,
    matchesTarget: matchesString(BTL),
    withAuthorization: { roles: [BUYER, SALES, ADMIN] }
  },
  {
    label: "Tier Type = 2 Star",
    key: TIER_TYPE,
    matchesTarget: matchesString(BBTL),
    withAuthorization: { roles: [BUYER, SALES, ADMIN] }
  },
  {
    label: "Tier Type = 1 Star",
    key: TIER_TYPE,
    matchesTarget: matchesString(ZTL),
    withAuthorization: { roles: [BUYER, SALES, ADMIN] }
  },
  {
    label: "Tier Type = 5 Star",
    key: TIER_TYPE,
    matchesTarget: matchesString(SATL),
    withAuthorization: { roles: [BUYER, SALES, ADMIN] }
  },
  {
    label: "Tier Type = Non-Qualifier",
    key: TIER_TYPE,
    matchesTarget: matchesString(""),
    withAuthorization: { roles: [BUYER, SALES, ADMIN] }
  },
  {
    label: "Active Transfer",
    key: "transfer"
  },
  {
    label: properCase(CONSIGNMENT),
    key: CONSIGNMENT,
    matchesTarget: matchesString(CONSIGNMENT)
  },
  {
    label: EMA.toUpperCase(),
    key: CONSIGNMENT,
    matchesTarget: matchesString(EMA)
  },
  {
    label: "Exclude Cons/EMAs",
    key: "exclude_consignment"
  },
  {
    label: "from lots",
    key: "lot"
  }
];

const Title = ({ applicationTags, currentUser, searchProps, buyersTags }) => {
  const filterTagsWithLocations = currentUser.locations.map(l => ({
    label: `Store Owner ${l.name}`,
    key: STORE_OWNER_ID,
    matchesTarget: matchesString(l.id)
  }));

  const titleText =
    hasAnyOfRoles(currentUser, [REPAIR, SALES]) &&
    ![ADMIN, QUALITY_CONTROL, ACCOUNTING].includes(currentUser.role) &&
    !hasPermission(currentUser, CAN_VIEW_ALL_INVENTORY)
      ? "My Inventory"
      : "All Inventory";
  return (
    <TitleWithFilters
      {...searchProps}
      filters={applicationTags
        .concat(filterTags)
        .concat(filterTagsWithLocations)
        .concat(buyersTags)}
      title={titleText}
    />
  );
};

Title.propTypes = {
  searchProps: SearchPropTypes,
  ...WithCurrentUser
};

export default withCurrentUser(Title);



// WEBPACK FOOTER //
// ./app/modules/inventories/components/Search/Title.jsx